import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type JSX, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getApiWaitingOrErrorUI } from "@/components/generic/errors";
import { MqttDataWaitingAlert } from "@/components/generic/mqtt-data-waiting-alert";
import { MultiPropertyCardList } from "@/components/generic/multi-property-card-list";
import { ActiveJobCard } from "@/components/milling-jobs/active-job-card";
import { RealTimeDataWaitingAlert } from "@/components/status/real-time-data-waiting-alert";

import { type ApiError, isApiWaitingOrError } from "@/data-interfaces/generics";
import type { ActiveMillingJobDetails } from "@/data-interfaces/milling-jobs";
import type {
	ActiveMillingJobBlockCompletion,
	RealTimeData,
} from "@/data-interfaces/real-time-data";
import { useMQTTPublish, useMQTTSubscribe } from "@/hooks/use-mqtt";
import { getJobBlocks } from "@/lib/data-remappings/active-job-details";
import {
	ACTIVE_JOB_DETAILS_PAGE_URL,
	getAppWrappedPageUrl,
	MILLING_JOBS_PAGE_URL,
} from "@/lib/page-routes";
import {
	getRtdSubscriptionTopic,
	MJ_ACTIVE_JOB_DETAILS_URL,
} from "@/lib/request-paths";
import { getMachineSerialNumber } from "@/lib/session-storage-keys";

export const Route = createFileRoute(
	getAppWrappedPageUrl(ACTIVE_JOB_DETAILS_PAGE_URL),
)({
	component: ActiveJobDetails,
});

function ActiveJobDetails() {
	const [t, _] = useTranslation();
	const [te, __] = useTranslation("apiErrors");

	const navigate = useNavigate();

	const [realTimeData, setRealTimeData] = useState<
		RealTimeData | ApiError | null
	>(null);
	const [jobDetails, setJobDetails] = useState<
		ActiveMillingJobDetails | ApiError | null
	>(null);

	const [currentMillingJobId, setCurrentMillingJobId] = useState<number | null>(
		null,
	);
	const [currentMillingJobBlockStep, setCurrentMillingJobBlockStep] = useState<
		number | null
	>(null);

	const publish = useMQTTPublish();

	useMQTTSubscribe(
		getRtdSubscriptionTopic(getMachineSerialNumber()),
		setRealTimeData,
		true,
	);
	useMQTTSubscribe(MJ_ACTIVE_JOB_DETAILS_URL, setJobDetails);

	const getMillingJobBlockStep = (
		blockCompletion: ActiveMillingJobBlockCompletion | null,
	): number | null => {
		return blockCompletion ? blockCompletion.step : null;
	};

	useEffect(() => {
		if (!isApiWaitingOrError(realTimeData)) {
			if (
				realTimeData.milling_job &&
				realTimeData.milling_job.id !== currentMillingJobId
			) {
				setCurrentMillingJobId(realTimeData.milling_job.id);
				setCurrentMillingJobBlockStep(
					getMillingJobBlockStep(realTimeData.milling_job.active_job_block),
				);
				publish(MJ_ACTIVE_JOB_DETAILS_URL, {
					job_id: realTimeData.milling_job.id,
				});
			} else if (
				realTimeData.milling_job &&
				realTimeData.milling_job.active_job_block?.step !==
					currentMillingJobBlockStep
			) {
				setCurrentMillingJobBlockStep(
					getMillingJobBlockStep(realTimeData.milling_job.active_job_block),
				);
				publish(MJ_ACTIVE_JOB_DETAILS_URL, {
					job_id: realTimeData.milling_job.id,
				});
			} else if (
				realTimeData.milling_job === null &&
				currentMillingJobId !== null
			) {
				setCurrentMillingJobId(null);
				setCurrentMillingJobBlockStep(null);
				setJobDetails({ blocks: [] });
			}
		}
	}, [realTimeData]);

	const getActiveJobUI = (): JSX.Element => {
		if (isApiWaitingOrError(realTimeData)) {
			return getApiWaitingOrErrorUI(
				realTimeData,
				"Error retrieving real-time data",
				te,
			);
		}

		if (realTimeData.milling_job === null) {
			return <div className="text-xl sm:text-3xl">{t("No active job")}</div>;
		}

		return (
			<>
				<ActiveJobCard active_job={realTimeData.milling_job} />
				<MqttDataWaitingAlert
					dataToWait={jobDetails}
					requestUrl={MJ_ACTIVE_JOB_DETAILS_URL}
					parameters={{ job_id: realTimeData.milling_job.id }}
				/>
			</>
		);
	};

	const getJobDetailsUI = (): JSX.Element => {
		if (isApiWaitingOrError(realTimeData)) {
			return getApiWaitingOrErrorUI(
				realTimeData,
				"Error retrieving real-time data",
				te,
			);
		}

		if (isApiWaitingOrError(jobDetails)) {
			return getApiWaitingOrErrorUI(
				jobDetails,
				"Error retrieving job details",
				te,
			);
		}

		return (
			<MultiPropertyCardList
				multiPropertyObjects={getJobBlocks(jobDetails, realTimeData)}
				noItemsLabel="No job block for active job"
				pagination={true}
			/>
		);
	};

	return (
		<div className="text-white p-8 flex flex-col flex-1 gap-y-4">
			<h2 className="text-2xl sm:text-3xl hover:text-[#238b8c]">
				<button
					type={"button"}
					onClick={() => {
						navigate({
							to: MILLING_JOBS_PAGE_URL,
							search: () => ({ selectedTab: "milling" }),
						});
					}}
				>
					{"< "} {t("Jobs")}
				</button>
			</h2>

			<div className="flex flex-col gap-y-4">
				<h2 className="text-2xl sm:text-3xl">{t("Selected job")}</h2>
				{getActiveJobUI()}
			</div>

			<div className="flex flex-col flex-1 grow gap-y-4">
				<h2 className="text-2xl sm:text-3xl">{t("Job details")}</h2>

				{getJobDetailsUI()}
			</div>

			<RealTimeDataWaitingAlert dataToWait={realTimeData} />
		</div>
	);
}
